import { useEffect, useState } from 'react';

import { api } from '../api/http.js';
import { useAdminAuth } from '../auth/AdminAuthContext.jsx';
import { useUI } from '../ui/UIProvider.jsx';
import { KpiTile } from '../components/KpiTile.jsx';
import { Icon } from '../components/icons.jsx';
import { CountryCell } from '../components/CountryFlag.jsx';
import { SignupsChart } from '../components/SignupsChart.jsx';

/**
 * Dashboard home. Composes four KPI tiles (totalUsers, MRR, open
 * queries, conversion %), a plan-distribution donut, a newest-signups
 * table, and a live-activity feed.
 *
 * Reads three endpoints in parallel on mount; nothing is mutated.
 * Errors per-endpoint are localised — a failing /audit/recent only
 * blanks the activity card, the rest of the dashboard still renders.
 *
 * @author <lukman@zshift.io>
 */
export function Overview() {
  const { me } = useAdminAuth();
  const { toast } = useUI();

  const [loading, setLoading] = useState(true);
  const [overview,    setOverview]    = useState(null);
  const [finOverview, setFinOverview] = useState(null);
  const [signups,     setSignups]     = useState([]);
  const [signupSeries, setSignupSeries] = useState([]);
  const [activity,    setActivity]    = useState([]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    // Parallel fetches — each wrapped in its own catch so one failure
    // doesn't kill the whole page. The KpiTile's loading prop falls
    // back to "—" when a section's data hasn't landed.
    const safe = (p, fallback) => p.catch((e) => { console.warn(e); return fallback; });

    Promise.all([
      safe(api('/api/admin/stats/overview'),     null),
      safe(api('/api/admin/finances/overview'),  null),
      safe(api('/api/admin/users?size=6'),       { items: [] }),
      safe(api('/api/admin/audit/recent'),       []),
      safe(api('/api/admin/stats/signups'),      []),
    ]).then(([o, f, u, a, sig]) => {
      if (cancelled) return;
      setOverview(o);
      setFinOverview(f);
      setSignups(u?.items || []);
      setActivity(Array.isArray(a) ? a : []);
      // Backend pre-Fix returns rows with `count`, post-Fix returns
      // `signups`. Normalise so the chart component sees a single
      // canonical shape regardless of which deploy is live.
      setSignupSeries(Array.isArray(sig)
        ? sig.map((r) => ({
            date:        r.date,
            signups:     r.signups     != null ? r.signups     : (r.count || 0),
            conversions: r.conversions != null ? r.conversions : 0,
          }))
        : []);
      setLoading(false);
    });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // KPI numbers (computed from the overview response).
  const totalNow = overview?.totalUsers     || 0;
  const last30   = overview?.usersLast30d   || 0;
  const prior30  = overview?.usersPrior30d  || 0;
  const pctChange = prior30 > 0
    ? ((last30 - prior30) / prior30) * 100
    : (last30 > 0 ? 100 : 0);
  const mrrPence = overview?.mrrPence || 0;
  // Defensive fallback for paying count. Two server versions in
  // flight:
  //   - Old (pre Fix #1) returns mrrPence but no payingPremium
  //     field; mrrPence is computed from raw tier count.
  //   - New returns both, with mrrPence based on the filtered
  //     payingPremium count.
  // If the field is missing, derive paying from mrrPence so the
  // two numbers on the MRR tile stay self-consistent regardless of
  // which deploy is live. £3.99 = 399p per paying user.
  const payingPremium = typeof overview?.payingPremium === 'number'
    ? overview.payingPremium
    : Math.round(mrrPence / 399);
  const open         = overview?.openQueries  || 0;
  const totalQueries = overview?.totalQueries || 0;
  // Premium adoption rate (NOT paid conversion). Uses the raw tier
  // count so admin-comped accounts count toward Premium adoption,
  // even though they don't show up in MRR. The server pre-rounds to
  // 1 decimal; we fall back to a live calc if a stale backend
  // version is still running the old payingPremium-based formula.
  const premiumOnTier = overview?.planCounts?.PREMIUM || 0;
  const conversion     = typeof overview?.conversionPct === 'number'
    ? overview.conversionPct
    : (totalNow > 0 ? (100 * premiumOnTier) / totalNow : 0);

  // Greet by first name from the signed-in admin (falls back to a neutral salutation).
  const firstName = me?.name ? me.name.split(/\s+/)[0] : null;
  const greeting = firstName ? <>Good morning, <em>{firstName}</em></> : <>Overview</>;

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1 className="page-title">{greeting}</h1>
          <p className="page-sub">Live counts from the production database. Refresh the page to re-fetch.</p>
        </div>
      </div>

      {/* ── KPI row ─────────────────────────────────────────────── */}
      <div className="kpi-row">
        <KpiTile
          label="Total users"
          iconName="users"
          value={totalNow}
          valueFormatter={(v) => v.toLocaleString()}
          delta={{
            pct: pctChange,
            direction: pctChange >= 0 ? 'up' : 'down',
            post: `vs prior 30d (${last30.toLocaleString()} new)`,
          }}
          loading={loading}
        />
        <KpiTile
          label="MRR"
          iconName="currency"
          iconColor="blue"
          value={mrrPence / 100}
          valueFormatter={(v) => '£' + v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          delta={{
            raw: payingPremium.toLocaleString() + ' paying',
            post: '· £3.99/mo each',
          }}
          loading={loading}
        />
        <KpiTile
          label="Open queries"
          iconName="queries"
          iconColor="orange"
          value={open}
          valueFormatter={(v) => String(v)}
          sub={`/ ${totalQueries >= 1000 ? (totalQueries / 1000).toFixed(1) + 'K' : totalQueries} total`}
          delta={{ post: 'across all time' }}
          loading={loading}
        />
        <KpiTile
          label="Basic → Premium"
          iconName="shield"
          iconColor="green"
          value={conversion}
          valueFormatter={(v) => v.toFixed(1) + '%'}
          delta={{
            // Sub-text uses the tier count (matches the numerator
            // shown in the percentage). For revenue context, look
            // at the MRR tile — its sub-text shows payingPremium.
            post: `${premiumOnTier.toLocaleString()} on Premium / ${totalNow.toLocaleString()} total`,
          }}
          loading={loading}
        />
      </div>

      {/* ── Signups vs paid conversions chart + plan donut ────── */}
      <div className="row r-2-1">
        <SignupsChart data={signupSeries} loading={loading} />
        <PlanDonutCard overview={overview} loading={loading} />
      </div>

      {/* ── Newest signups table ───────────────────────────────── */}
      <div className="row">
        <NewestSignupsCard users={signups} loading={loading} />
      </div>

      {/* ── Live activity ─────────────────────────────────────── */}
      <div className="row">
        <ActivityCard activity={activity} loading={loading} />
      </div>
    </div>
  );
}

// ── Plan-distribution donut (driven by planCounts) ───────────────
//
// SVG arc math: stroke-dasharray "X (100-X)" + stroke-dashoffset
// positions a partial-circle arc starting at the top (after the
// -90deg rotate). For multiple arcs that should sit contiguously:
//
//   arc1.dashoffset = 25                       — starts at top
//   arc2.dashoffset = 25 - arc1.pct            — starts where arc1 ends
//   arc3.dashoffset = arc2.dashoffset - arc2.pct
//   ...
//
// The radius (15.915) is chosen so 2πr ≈ 100, making each dasharray
// value read as "% of the circle". Strips out fractional bookkeeping
// from the code.
function PlanDonutCard({ overview, loading }) {
  // Authoritative total = the User row count. Premium count comes
  // from the tier-grouped query (only rows where tier='PREMIUM').
  // Basic is derived as everyone-else: total - premium. This way
  // users whose tier column is NULL (legacy rows from before the
  // column existed, or rows where the default never landed) are
  // bucketed into Basic — which matches the product semantics: in a
  // 2-tier system, anyone who isn't Premium IS Basic. Without this
  // adjustment, the donut underreports total accounts and looks
  // inconsistent with the "Total users" KPI tile.
  const totalUsers = overview?.totalUsers || 0;
  const premium    = overview?.planCounts?.PREMIUM || 0;
  const basic      = Math.max(0, totalUsers - premium);
  const total      = totalUsers;   // shown in the donut centre
  const hasData    = total > 0;

  // Real percentages — exact (no .toFixed yet) so the math at the
  // join stays continuous.
  const basicPct   = hasData ? (100 * basic)   / total : 0;
  const premiumPct = hasData ? (100 * premium) / total : 0;

  // Pre-format the localised counts once.
  const fmt = (n) => n.toLocaleString();

  return (
    <div className="card donut-card">
      <h3 style={{ alignSelf: 'flex-start', margin: 0 }}>Plan distribution</h3>
      <div className="donut-wrap">
        <svg viewBox="0 0 42 42" width="200" height="200" role="img" aria-label="Plan distribution donut chart">
          {/* Ring track (always present so even an empty DB shows
              a clean circle rather than a hollow gap). */}
          <circle cx="21" cy="21" r="15.915" fill="none" stroke="#ECE3F7" strokeWidth="6"/>

          {/* Arcs only render when we have data AND that tier has
              users — avoids the misleading 50/50 placeholder, and
              skips invisible 0-length arcs that SVG would otherwise
              compute over. */}
          {hasData && basic > 0 && (
            <circle cx="21" cy="21" r="15.915" fill="none" stroke="#C7BCD8" strokeWidth="6"
                    strokeDasharray={`${basicPct.toFixed(3)} ${(100 - basicPct).toFixed(3)}`}
                    strokeDashoffset="25"
                    transform="rotate(-90 21 21)">
              <title>{`Basic — ${fmt(basic)} users · ${basicPct.toFixed(1)}%`}</title>
            </circle>
          )}
          {hasData && premium > 0 && (
            <circle cx="21" cy="21" r="15.915" fill="none" stroke="#6B3FD4" strokeWidth="6"
                    strokeDasharray={`${premiumPct.toFixed(3)} ${(100 - premiumPct).toFixed(3)}`}
                    strokeDashoffset={(25 - basicPct).toFixed(3)}
                    transform="rotate(-90 21 21)">
              <title>{`Premium — ${fmt(premium)} users · ${premiumPct.toFixed(1)}%`}</title>
            </circle>
          )}
        </svg>
        <div className="donut-center">
          <span className="v">
            {loading
              ? '—'
              : (total >= 10000 ? (total / 1000).toFixed(1) + 'K' : fmt(total))}
          </span>
          <span className="lbl">Total accounts</span>
        </div>
      </div>
      <div className="donut-legend">
        <DonutRow color="#C7BCD8" name="Basic"   value={basic}
                  pct={hasData ? basicPct   : null} loading={loading} />
        <DonutRow color="#6B3FD4" name="Premium" value={premium}
                  pct={hasData ? premiumPct : null} loading={loading} />
      </div>
    </div>
  );
}

function DonutRow({ color, name, value, pct, loading }) {
  return (
    <div className="donut-leg-row">
      <span className="sw" style={{ background: color }} />
      <span className="nm">{name}</span>
      {pct != null && !loading && (
        <span className="mono" style={{ color: 'var(--ink-3)', fontSize: 11, marginRight: 8 }}>
          {pct.toFixed(1)}%
        </span>
      )}
      <span className="nb">{loading ? '—' : value.toLocaleString()}</span>
    </div>
  );
}

// ── Newest signups table ─────────────────────────────────────────
function NewestSignupsCard({ users, loading }) {
  return (
    <div className="card">
      <div className="card-head">
        <h3>Newest signups</h3>
        <NavGhost to="/users">View all →</NavGhost>
      </div>
      <div className="table-wrap">
        <table className="t">
          <thead>
            <tr>
              <th>User</th>
              <th>Country</th>
              <th>Plan</th>
              <th>Joined</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={4} style={{ padding: 40, textAlign: 'center', color: 'var(--ink-3)' }}>Loading…</td></tr>
            )}
            {!loading && users.length === 0 && (
              <tr><td colSpan={4} style={{ padding: 40, textAlign: 'center', color: 'var(--ink-3)' }}>No users yet.</td></tr>
            )}
            {!loading && users.map((u, i) => (
              <tr key={u.id}>
                <td>
                  <div className="user-cell">
                    <div className={`av v${(u.id % 10) + 1}`}>{initials(u.name || u.email)}</div>
                    <div>
                      <strong>{u.name || '—'}</strong>
                      <em>{u.email}</em>
                    </div>
                  </div>
                </td>
                <td><CountryCell code={u.country} /></td>
                <td>
                  <span className={`pill ${u.tier === 'PREMIUM' ? 'plus' : 'free'}`}>
                    <span className="dot"/> {u.tier}
                  </span>
                </td>
                <td className="mono" style={{ color: 'var(--ink-3)' }}>
                  {u.createdAt ? agoString(u.createdAt) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Activity feed ────────────────────────────────────────────────
function ActivityCard({ activity, loading }) {
  return (
    <div className="card">
      <div className="card-head">
        <h3>Recent admin actions</h3>
        <span className="pill success"><span className="dot"/> LIVE</span>
      </div>
      <div className="activity">
        {loading && <div style={{ padding: 40, textAlign: 'center', color: 'var(--ink-3)' }}>Loading…</div>}
        {!loading && activity.length === 0 && (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--ink-3)' }}>No recent activity.</div>
        )}
        {!loading && activity.map((a) => {
          const h = humanizeAction(a);
          return (
            <div className="act-item" key={a.id}>
              <div className={`act-ic ${h.color}`}>
                <Icon name={h.icon} size={14} />
              </div>
              <div className="act-body">
                <strong>{h.actor}</strong>{' '}
                <span style={{ color: 'var(--ink-2)' }}>{h.verb}</span>
                {h.object && (
                  <>
                    {' '}<span className="mono" style={{ color: 'var(--ink-3)' }}>{h.object}</span>
                  </>
                )}
                <span className="act-time">{a.createdAt ? agoString(a.createdAt) : '—'}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Turn a raw AuditLog row into a sentence + icon + colour. Every
 * action emitted by AuditLogService is enumerated below; unknown
 * action codes fall through to a sensible default so the feed never
 * shows a "[object Object]" or a raw code as the entire payload.
 *
 *   audit.record(admin, "ban_user", "user:42", ...)
 *   → { actor: "smilewithjaji@…", verb: "banned", object: "user:42",
 *       icon: "ban", color: "red" }
 *
 * The verb is past-tense English. The object is the audit row's
 * `target` field, kept in mono since it's an ID; future iterations
 * could resolve IDs to names (e.g. "ticket #5") by joining against
 * the relevant table.
 */
function humanizeAction(a) {
  const code = a.action || '';
  const target = a.target || '';
  const actor = a.actorEmail || (a.actorId == null ? 'system' : `admin#${a.actorId}`);

  // Every known action code from AuditLogService call sites, mapped
  // to (verb, icon, color). Keys are matched exactly — fall through
  // to the default at the bottom for anything new.
  const MAP = {
    // Auth
    admin_login:           { verb: 'signed in',                       icon: 'check',   color: 'blue'   },
    bootstrap_admin:       { verb: 'was seeded as the first admin',   icon: 'shield',  color: 'green'  },

    // Admin-team management
    invite_admin:          { verb: 'invited',                         icon: 'mail',    color: 'green'  },
    change_admin_role:     { verb: 'changed role of',                 icon: 'shield',  color: 'blue'   },
    disable_admin:         { verb: 'disabled',                        icon: 'ban',     color: 'red'    },
    enable_admin:          { verb: 'enabled',                         icon: 'check',   color: 'green'  },
    delete_admin:          { verb: 'deleted',                         icon: 'ban',     color: 'red'    },

    // User actions
    ban_user:              { verb: 'banned',                          icon: 'ban',     color: 'red'    },
    unban_user:            { verb: 'un-banned',                       icon: 'check',   color: 'green'  },
    change_plan:           { verb: 'changed plan of',                 icon: 'plans',   color: 'blue'   },

    // Support
    reply_query:           { verb: 'replied to',                      icon: 'queries', color: 'blue'   },
    close_query:           { verb: 'closed',                          icon: 'check',   color: 'green'  },

    // Finances
    refund_intent:         { verb: 'initiated a refund for',          icon: 'finances',color: 'orange' },

    // Plans
    edit_plan_catalog_draft: { verb: 'edited the plan catalog',       icon: 'plans',   color: 'blue'   },
    force_plan_reconcile:    { verb: 'force-ran plan reconciler',     icon: 'refresh', color: 'blue'   },
    plan_expired_downgrade:  { verb: 'auto-downgraded',               icon: 'arrowDown', color: 'orange' },

    // Self-service settings (the actor + the target are the same row;
    // the humaniser at the bottom strips the redundant `target` when
    // the action is self-acting, matching the bootstrap_admin /
    // edit_plan_catalog_draft pattern).
    change_admin_password:   { verb: 'changed their password',        icon: 'lock',    color: 'blue'   },
    update_admin_profile:    { verb: 'updated their profile',         icon: 'user',    color: 'blue'   },
  };

  const hit = MAP[code];
  if (hit) {
    return {
      actor,
      verb: hit.verb,
      // For self-acting events (login, bootstrap, catalog edit),
      // the target IS the actor — don't repeat it in mono.
      object: (code === 'admin_login'
            || code === 'bootstrap_admin'
            || code === 'edit_plan_catalog_draft'
            || code === 'change_admin_password'
            || code === 'update_admin_profile')
        ? '' : target,
      icon: hit.icon,
      color: hit.color,
    };
  }

  // Unknown action — fall back to readable best-effort. Turns
  // "some_new_action" into "some new action".
  return {
    actor,
    verb: code.replace(/_/g, ' ') || 'did something',
    object: target,
    icon: 'logs',
    color: '',
  };
}

// ── Local helpers ────────────────────────────────────────────────
function initials(name) {
  if (!name) return '—';
  return name.split(/\s+/).filter(Boolean).map((p) => p[0]).slice(0, 2).join('').toUpperCase();
}
function agoString(iso) {
  const ts = new Date(iso).getTime();
  if (!ts) return '—';
  const mins = Math.max(0, Math.floor((Date.now() - ts) / 60000));
  if (mins < 1) return 'now';
  if (mins < 60) return mins + 'm ago';
  const h = Math.floor(mins / 60);
  if (h < 24) return h + 'h ago';
  const d = Math.floor(h / 24);
  if (d < 30) return d + 'd ago';
  return Math.floor(d / 30) + 'mo ago';
}
// Tiny NavLink wrapper that renders as a ghost button for the
// "View all →" link in card headers.
function NavGhost({ to, children }) {
  return <a href={to} className="btn ghost sm" onClick={(e) => { e.preventDefault(); window.history.pushState({}, '', to); window.dispatchEvent(new Event('popstate')); }}>{children}</a>;
}
